/**
 * Collection Filter Manager
 * Handles AJAX filtering of products in Shopify collections
 */
class CollectionFilterManager {
  constructor(options = {}) {
    this.config = {
      formId: "CollectionFiltersForm",
      sectionId: "static-collection",
      gridSelector: ".collection_container_description_plp",
      filterSectionSelector: "#filters",
      chipsSelector: ".sfchips",
      filterSelector: ".sfbar",
       headerCallSelector: ".header-call",
      toggleSelector: ".sftoggle",
      sheetSelector: "#sfsheet",
      overlaySelector: ".sfoverlay",
      headerSelector: "[data-site-header]",
      mobileBreakpoint: 768,
      popupElementSelector: '[data-float-off]',
      ...options,
    };

    this.elements = {};
    this.isLoading = false;

    this.init();
  }

  /**
   * Initializes the filter manager
   * Sets up all necessary DOM elements and event listeners
   */
  init() {
    if (!this.findElements()) {
      console.warn("CollectionFilterManager: Form element not found");
      return;
    }
    this.calcHeightSticky();
    this.setupStickyHeightObserver();
    this.bindEvents();
    this.bindInitialLinks();
    this.initializeFilterDetails();
    this.initializeSheet();
  }

  /**
   * Dynamically adjusts the sticky height of the filter container
   * to match the current height of the header.
   */
  calcHeightSticky() {
    const { header, filterSectionContainer } = this.elements;
    if (!header || !filterSectionContainer) return;

    const headerHeight = header.getBoundingClientRect().height;

    filterSectionContainer.style.setProperty(
      "--height-top-sticky",
      `${headerHeight}px`
    );
  }

  /* Keeps the sticky offset in sync when the header resizes or scroll state changes. */
  setupStickyHeightObserver() {
    const { header } = this.elements;
    if (!header) return;

    if (this.stickyResizeObserver) {
      this.stickyResizeObserver.disconnect();
    }

    this.stickyResizeObserver = new ResizeObserver(() => {
      this.calcHeightSticky();
    });
    this.stickyResizeObserver.observe(header);

    if (this.stickyClassObserver) {
      this.stickyClassObserver.disconnect();
    }

    this.stickyClassObserver = new MutationObserver(() => {
      this.calcHeightSticky();
    });
    this.stickyClassObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  /**
   * Finds and caches DOM elements
   * @returns {boolean} True if required form element is found
   */
  findElements() {
    this.elements = {
      form: document.getElementById(this.config.formId),
      grid: document.querySelector(this.config.gridSelector),
      chipsContainer: document.querySelector(this.config.chipsSelector),
      filterContainer: document.querySelector(this.config.filterSelector),
      headerCall: document.querySelector(this.config.headerCallSelector),
      filterSectionContainer: document.querySelector(
        this.config.filterSectionSelector
      ),
      toggleButton: document.querySelector(this.config.toggleSelector),
      sheet: document.querySelector(this.config.sheetSelector),
      overlay: document.querySelector(this.config.overlaySelector),
      header: document.querySelector(this.config.headerSelector),
      popupElement: document.querySelector(this.config.popupElementSelector),
    };
    return !!this.elements.form;
  }

  /**
   * Binds all necessary event listeners
   * Handles form changes, submissions, browser navigation, and keyboard events
   */
  bindEvents() {
    this.elements.form.addEventListener(
      "change",
      this.handleFormSubmit.bind(this)
    );
    this.elements.form.addEventListener(
      "submit",
      this.handleFormSubmit.bind(this)
    );
    window.addEventListener("popstate", this.handlePopState.bind(this));
    document.addEventListener("click", this.handleDocumentClick.bind(this));
    window.addEventListener("keydown", this.handleKeyDown.bind(this));
    window.addEventListener("resize", this.calcHeightSticky.bind(this))
  }

  /**
   * Handles form submission events
   * Prevents default submission and applies filters via AJAX
   * @param {Event} event - The form submission event
   */
  async handleFormSubmit(event) {
    event.preventDefault();
    await this.applyFiltersFromForm();
  }

  /**
   * Reinitializes components after DOM updates
   * Restarts lazy loading, notifies theme, and rebinds events
   */
  reinitializeComponents() {
    const root = this.findCurrentSection() || document;

    this.reinitializeLazyLoading(root);
    this.notifyTheme(root);
    this.init();
  }

  /**
   * Handles browser back/forward navigation
   * Updates the page content when user navigates through history
   * @param {PopStateEvent} event - The popstate event
   */
  async handlePopState(event) {
    await this.fetchAndUpdateSection(location.href);
  }

  /**
   * Handles document clicks to close open filter details
   * Closes any open filter dropdown when clicking outside of them
   * @param {Event} event - The click event
   */
  handleDocumentClick(event) {
    const openDetails = document.querySelectorAll(".sfbar details[open]");
    const clickedInsideAnyDetail = [...openDetails].some((detail) =>
      detail.contains(event.target)
    );

    if (!clickedInsideAnyDetail) {
      openDetails.forEach((detail) => (detail.open = false));
    }
  }

  /**
   * Handles keyboard events
   * Closes the filter sheet when Escape key is pressed
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown(event) {
    if (event.key === "Escape") {
      this.closeSheet();
    }
  }

  /**
   * Applies filters from the form data
   * Builds new URL from form and updates the page content
   */
  async applyFiltersFromForm() {
    if (this.isLoading) return;

    const newUrl = this.buildUrlFromForm();
    this.updateHistory(newUrl);
    await this.fetchAndUpdateSection(newUrl);
  }

  /**
   * Builds URL with query parameters from form data
   * Handles special cases like price filters and proper URL encoding
   * @returns {string} The constructed URL with filter parameters
   */
  buildUrlFromForm() {
    const formData = new FormData(this.elements.form);
    const params = new URLSearchParams();

    for (const [key, value] of formData.entries()) {
      if (key == "price") {
        let priceValueSplit = value.split("&");
        priceValueSplit.forEach((paramPair) => {
          const [priceKey, priceValue] = paramPair.split("=");
          if (priceKey?.trim() && priceValue?.trim()) {
            const decodedKey = decodeURIComponent(priceKey.trim());
            const decodedValue = decodeURIComponent(priceValue.trim());
            params.append(decodedKey, decodedValue);
          }
        });
      } else if (value !== "") {
        const decodedKey = decodeURIComponent(key.trim());
        const decodedValue = decodeURIComponent(value.trim());
        params.append(decodedKey, decodedValue);
      }
    }

    return `${window.location.pathname}?${params.toString()}`;
  }

  /**
   * Updates the browser history state
   * Pushes new state to allow proper back/forward navigation
   * @param {string} url - The URL to add to browser history
   */
  updateHistory(url) {
    history.pushState({ ajax: true }, "", url);
  }

  /**
   * Fetches and updates the collection section
   * Makes AJAX request and updates DOM with new content
   * @param {string} url - The URL to fetch content from
   */
  async fetchAndUpdateSection(url) {
    if (this.isLoading) return;

    try {
      this.setLoadingState(true);
      const response = await this.fetchSectionData(url);
      const htmlContent = await response.text();

      this.updateDOM(htmlContent);
      this.reinitializeComponents();
    } catch (error) {
      console.error("Error fetching section:", error);
      this.handleFetchError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  /**
   * Fetches section data via AJAX
   * Makes XMLHttpRequest to get filtered collection content
   * @param {string} url - The URL to fetch data from
   * @returns {Promise<Response>} The fetch response
   */
  async fetchSectionData(url) {
    const requestUrl = new URL(url, window.location.origin);

    return fetch(requestUrl.toString(), {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });
  }

  /**
   * Updates DOM with new HTML content
   * Replaces specific sections while preserving overall page structure
   * @param {string} htmlContent - The new HTML content to insert
   */
  updateDOM(htmlContent) {
    const template = document.createElement("template");
    template.innerHTML = htmlContent.trim();

    const updates = [
      { container: this.elements.grid, selector: this.config.gridSelector },
      {
        container: this.elements.chipsContainer,
        selector: this.config.chipsSelector,
      },
      {
        container: this.elements.filterContainer,
        selector: this.config.filterSelector,
      },
      {
        container: this.elements.filterSectionContainer,
        selector: this.config.filterSectionSelector,
      },
    ];

    // Execute all DOM updates
    updates.forEach(({ container, selector }) => {
      this.updateElementDOM(template, container, selector);
    });
    this.closeSheet();
  }

  /**
   * Updates a specific DOM element with new content
   * @param {HTMLTemplateElement} template - Template containing new content
   * @param {HTMLElement} container - The container element to update
   * @param {string} selector - CSS selector to find content in template
   */
  updateElementDOM(template, container, selector) {
    const content = template.content.querySelector(selector);

    if (content && container) {
      container.innerHTML = content.innerHTML;
    }
  }

  /**
   * Finds the current collection section element
   * @returns {HTMLElement|null} The section element or null if not found
   */
  findCurrentSection() {
    return (
      document.querySelector(`[data-section-id="${this.config.sectionId}"]`) ||
      document.getElementById(`shopify-section-${this.config.sectionId}`)
    );
  }

  /**
   * Initializes filter accordion functionality
   * Sets up click handlers for filter dropdown summaries
   */
  initializeFilterDetails() {
    const summaries = document.querySelectorAll(".sfbar details > summary");

    summaries.forEach((summary) => {
      // Remove previous listener if exists
      summary.removeEventListener("click", this.handleSummaryClick);
      summary.addEventListener("click", this.handleSummaryClick.bind(this));
    });
  }

  /**
   * Handles clicks on filter summary elements
   * Closes other open filter details when one is clicked
   * @param {Event} event - The click event
   */
  handleSummaryClick(event) {
    const currentDetail = event.currentTarget.parentNode;

    // Close all other details
    document.querySelectorAll(".sfbar details").forEach((detail) => {
      if (detail !== currentDetail) {
        detail.open = false;
      }
    });
  }

  /**
   * Initializes mobile filter sheet functionality
   * Sets up open/close event handlers for the modal filter sheet
   */
  initializeSheet() {
    if (
      !this.elements.toggleButton ||
      !this.elements.sheet ||
      !this.elements.overlay
    ) {
      return;
    }

    // Bind open event
    this.elements.toggleButton.removeEventListener("click", this.openSheet);
    this.elements.toggleButton.addEventListener(
      "click",
      this.openSheet.bind(this)
    );

    // Bind close events
    const closeButtons = document.querySelectorAll("[data-close-sheet]");
    closeButtons.forEach((button) => {
      button.removeEventListener("click", this.closeSheet);
      button.addEventListener("click", this.closeSheet.bind(this));
    });
  }

  /**
   * Opens the mobile filter sheet modal
   * Shows the filter sheet and overlay, prevents body scrolling
   */
  openSheet() {
    if (!this.elements.sheet || !this.elements.overlay) return;

    this.elements.sheet.setAttribute("open", "");
    this.elements.overlay.setAttribute("open", "");
    document.body.classList.add("noscroll", "sheet-active");

    if (this.elements.toggleButton) {
      this.elements.toggleButton.setAttribute("aria-expanded", "true");
    }
    if (this.elements.popupElement) {
      this.elements.popupElement.classList.add("is-hidden");
    }
  }

  /**
   * Closes the mobile filter sheet modal
   * Hides the filter sheet and overlay, restores body scrolling
   */
  closeSheet() {
    if (!this.elements.sheet || !this.elements.overlay) return;

    this.elements.sheet.removeAttribute("open");
    this.elements.overlay.removeAttribute("open");
    document.body.classList.remove("noscroll", "sheet-active");

    if (this.elements.toggleButton) {
      this.elements.toggleButton.setAttribute("aria-expanded", "false");
    }
    if (this.elements.popupElement) {
      this.elements.popupElement.classList.remove("is-hidden");
    }
  }

  /**
   * Reinitializes lazy loading for images
   * Triggers lazy loading check for newly loaded content
   * @param {HTMLElement} root - The root element to scan for lazy images
   */
  reinitializeLazyLoading(root) {
    const lazyImages = root.querySelectorAll(
      'img[data-src], img[data-lowsrc], img[data-widths], img[loading="lazy"]'
    );

    if (window.lazySizes?.loader) {
      window.lazySizes.loader.checkElems();
      lazyImages.forEach((img) => window.lazySizes.loader.unveil(img));
    } else {
      // Fallback for native lazy loading
      lazyImages.forEach((img) => {
        if (img.dataset.src && !img.src) {
          img.src = img.dataset.src;
        }
      });
    }
  }

  /**
   * Binds click events to clear filter links and chips
   * @param {HTMLElement} root - The root element to search within
   */
  bindClearLinks(root = document) {
    const clearElements = root.querySelectorAll(
      ".sfmenu__clear, .sfbar__clearall a, .sfchip"
    );

    clearElements.forEach((element) => {
      // Remove previous listener if exists
      element.removeEventListener("click", this.handleClearClick);
      element.addEventListener("click", this.handleClearClick.bind(this));
    });
  }

  /**
   * Handles clicks on clear filter elements
   * Clears filters and updates the page content
   * @param {Event} event - The click event
   */
  async handleClearClick(event) {
    event.preventDefault();

    const href = event.currentTarget.getAttribute("href");
    if (!href) return;

    this.updateHistory(href);
    await this.fetchAndUpdateSection(href);
  }

  /**
   * Binds click events to pagination links
   * Makes pagination work with AJAX instead of full page reloads
   */
  bindPaginationLinks() {
    const paginationLinks = document.querySelectorAll(
      `${this.config.gridSelector} a[href*="page="]`
    );

    paginationLinks.forEach((link) => {
      link.removeEventListener("click", this.handlePaginationClick);
      link.addEventListener("click", this.handlePaginationClick.bind(this));
    });
  }

  /**
   * Handles clicks on pagination links
   * Loads new page content via AJAX and scrolls to product grid
   * @param {Event} event - The click event
   */
  async handlePaginationClick(event) {
    event.preventDefault();

    const href = event.currentTarget.getAttribute("href");
    if (!href) return;

    const finalUrl = new URL(href, location.origin).toString();
    this.updateHistory(finalUrl);

    await this.fetchAndUpdateSection(finalUrl);
    this.scrollToGrid();
  }

  /**
   * Scrolls the page to the product grid
   * Provides smooth scrolling to the top of the product grid
   */
  scrollToGrid() {
    const grid = document.querySelector(this.config.gridSelector);
    if (grid) {
      grid.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  /**
   * Binds all initial link event handlers
   * Called during initialization to set up clear and pagination links
   */
  bindInitialLinks() {
    this.bindClearLinks();
    this.bindPaginationLinks();
  }

  /**
   * Sets the loading state of the filter manager
   * Adds/removes loading classes and prevents multiple simultaneous requests
   * @param {boolean} loading - Whether the manager is currently loading
   */
  setLoadingState(loading) {
    this.isLoading = loading;
    this.elements.grid?.classList.toggle("ajax-loading", loading);
  }

  /**
   * Notifies the theme about section changes
   * Dispatches events and calls theme initialization functions if available
   * @param {HTMLElement} root - The root element that was updated
   */
  notifyTheme(root) {
    try {
      // Custom event for theme
      document.dispatchEvent(
        new CustomEvent("shopify:section:load", {
          detail: { sectionId: this.config.sectionId },
        })
      );

      // Theme initialization if available
      window.theme?.init?.();
      window.theme?.initProductCards?.(root);
    } catch (error) {
      console.warn("Theme notification failed:", error);
    }
  }

  /**
   * Handles fetch errors
   * Logs errors and could be extended to show user notifications
   * @param {Error} error - The error that occurred during fetch
   */
  handleFetchError(error) {
    console.error("Collection filter error:", error);
    // Here you could show a user notification
  }
}

// Automatic initialization when DOM is ready
(function () {
  /**
   * Initializes the collection filter manager
   * Creates a global instance for debugging if necessary
   */
  function initCollectionFilter() {
    // Create global instance for debugging if needed
    window.collectionFilterManager = new CollectionFilterManager();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCollectionFilter);
  } else {
    initCollectionFilter();
  }
})();
